import type { ReactElement } from 'react';
import { Badge, Box, Divider, Group, Text } from '@mantine/core';
import { SurfaceCard } from '@/design/SurfaceCard';
import { formatPercent, formatTokens, formatUsd } from '@/shared/analytics/MetricFormatting';
import { scoreColor } from '@/design/ScoreColors';

interface SessionDriftEfficiencyCardProps {
    efficiency: number;
    driftTokens: number;
    totalTokens: number;
    /** Allocated $ across all this person's turns; 0 when no cost data covers them. */
    totalCostUsd: number;
    /** Allocated $ burned on drifted turns; 0 when no cost data covers them. */
    driftCostUsd: number;
}

/**
 * The session-drift side rail hero: the share of tokens this person spent on the
 * session's main topic, shown as a headline percentage and a proportion bar (the
 * filled portion is on-topic spend, the remainder is drift). Cost impact prices
 * the drift from the interactions' allocated real spend; when no cost data
 * covers the window yet it shows a "no cost data" badge instead of $0.
 */
export default function SessionDriftEfficiencyCard({ efficiency, driftTokens, totalTokens, totalCostUsd, driftCostUsd }: SessionDriftEfficiencyCardProps): ReactElement {
    const color = scoreColor(efficiency * 100);
    const onTopicTokens = Math.max(0, totalTokens - driftTokens);

    return (
        <SurfaceCard $padding="md">
            <Text size="xs" fw={700} tt="uppercase" c="dimmed" style={{ letterSpacing: '0.06em' }}>
                Efficiency
            </Text>
            <Text fw={700} ff="monospace" style={{ fontSize: '30px', lineHeight: 1.1, letterSpacing: '-0.03em', color }}>
                {formatPercent(efficiency)}
            </Text>
            <Text size="xs" c="dimmed" mt={2}>
                Share of tokens spent on-topic, not lost to drift
            </Text>

            <Box mt="md" style={{ display: 'flex', height: 8, borderRadius: 999, background: 'var(--bm-color-surface-alt)', overflow: 'hidden' }}>
                <Box style={{ width: `${Math.round(efficiency * 100)}%`, height: '100%', background: color }} />
            </Box>
            <Group justify="space-between" mt={6}>
                <Text size="xs" c="dimmed" ff="monospace">
                    {formatTokens(onTopicTokens)} on-topic
                </Text>
                <Text size="xs" c="dimmed" ff="monospace">
                    {formatTokens(driftTokens)} drift
                </Text>
            </Group>

            <Divider my="md" />

            <Group justify="space-between" align="center">
                <Text size="xs" fw={700} tt="uppercase" c="dimmed" style={{ letterSpacing: '0.06em' }}>
                    Cost impact
                </Text>
                {totalCostUsd > 0 ?
                    <Text size="sm" fw={700} ff="monospace" style={{ color }}>
                        {formatUsd(driftCostUsd)}
                    </Text>
                :   <Badge size="sm" radius="sm" variant="light" color="gray">
                        No cost data
                    </Badge>
                }
            </Group>
            {totalCostUsd > 0 && (
                <Text size="xs" c="dimmed" mt={2}>
                    Allocated spend burned on drift, of {formatUsd(totalCostUsd)} total
                </Text>
            )}
        </SurfaceCard>
    );
}
